<template>
    
    <div>
        <div class="card-transactions-container">
            <div class="card-transactions-header">
                <div class="card-transactions-header-date">
                    <h5>From</h5>
                    <input type="date" v-model="dateFrom" @change="handleDateChange" class="date-input" />
                    <h6>to</h6>
                    <input type="date" v-model="dateTo" @change="handleDateChange" class="date-input" />
                </div>
                <div>
                    <button class="download-button" @click="downloadTransactions">Download selected range <i class="bi bi-download ms-2"></i></button>
                </div>
            </div>
            <div class="card-transactions-body">
                <table class="card-transactions-table">
                    <thead>
                        <tr>
                            <th v-for="tab in tableTabs"
                                :key="tab.key"
                                :class="{'active-table-tab' : activeTableTab === tab.key}"
                                @click="setActiveTableTab(tab.key)"
                            >
                            <div class="d-flex justify-content-between">
                                {{ tab.label }}
                                <div>
                                    <i v-if="activeTableTab === tab.key && sortOrder === 'asc'" class="fa fa-sort-asc"></i>
                                    <i v-else-if="activeTableTab === tab.key && sortOrder === 'desc'" class="fa fa-sort-desc"></i>
                                    <i v-else class="fa fa-sort muted"></i>
                                </div>
                            </div>
                            </th>
                            
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!sortedTransactions || sortedTransactions.length === 0">
                            <td :colspan="tableTabs.length" style="text-align: center; padding: 40px;">
                                No transactions found
                            </td>
                        </tr>
                        <tr v-for="transaction in sortedTransactions" :key="transaction.id">
                            <td>{{ formatDateTime(transaction.occurred_at) }}</td>
                            <td>{{ getStatusFromType(transaction.type) }}</td>
                            <td>{{ transaction.type_description || transaction.type || 'N/A' }}</td>
                            <td>{{ transaction.type || 'N/A' }}</td>
                            <td>{{ transaction.card_acceptor_location || 'N/A' }}</td>
                            <td :class="getAmountClass(transaction.amount)">{{ formatSignedAmount(transaction.amount) }}</td>
                            <td>{{ formatAmount(transaction.amount) }}</td>
                            <td>{{ transaction.reference || transaction.id || 'N/A' }}</td>
                        </tr>
                    </tbody>
                </table>
                <div>
                <div class="pagination-container">
                 <div class="pagination-wrapper">
                    <div @click="changePage(1)" style="cursor: pointer;"><img  src="@/assets/images/admin/arrow/prev-end.png" alt="Prev-end" style="width: 25px; height: 25px;" /></div>
                    <div class="pagination-left" @click="changePage(Math.max(1, paginationInfo.current - 1))" style="cursor: pointer;"> <img src="@/assets/images/admin/arrow/prev.png" alt="prev" style="width: 25px; height: 25px;" > </div>
                    <span>
              Page
              <input type="number" style="width: 60px;" v-model.number="currentPageInput" @change="changePage(currentPageInput)" min="1" :max="paginationInfo.total" />

              of {{ paginationInfo.total }}
            </span>
                    <div class="pagination-right" @click="changePage(Math.min(paginationInfo.total, paginationInfo.current + 1))" style="cursor: pointer;"> <img  src="@/assets/images/admin/arrow/next.png" alt="Next" style="width: 25px; height: 25px;" /> </div>
                    <div @click="changePage(paginationInfo.total)" style="cursor: pointer;"><img  src="@/assets/images/admin/arrow/next-end.png" alt="Next-end" style="width: 25px; height: 25px;" /></div>
                </div>
                <div>
                    <p v-if="paginationInfo.totalItems > 0">
                        Displaying transactions {{ paginationInfo.start }} - {{ paginationInfo.end }} of {{ paginationInfo.totalItems }}
                    </p>
                    <p v-else>No transactions to display</p>
                </div>
                </div>
</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import { useCardStore } from '@/store/admin/card';
import { formatDateTime, formatAmount, formatDateForInput, formatDateForAPI } from '@/utils/formatters';

const cardStore = useCardStore();

const tableTabs = [
    {key: 'tx date/time', label: 'TX Date/Time' },
    {key: 'status', label: 'Status' },
    {key: 'type', label: 'Type' },
    {key: 'mcc', label: 'MCC' },
    {key: 'merchant name', label: 'Merchant name' },
    {key: 'amount', label: 'Amount' },
    {key: 'amount in aud', label: 'Amount in AUD' },
    {key: 'reference', label: 'Reference' },
];

const activeTableTab = ref(tableTabs[0].key);
const sortOrder = ref('desc'); // 'asc' 或 'desc'

const setActiveTableTab = (tabKey) => {
    if (activeTableTab.value === tabKey) {
        // 如果點擊同一個標題，切換排序順序
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        // 如果點擊不同的標題，設置新的標題並重置為降序
        activeTableTab.value = tabKey;
        sortOrder.value = 'desc';
    }
}

// 排序後的交易資料
const sortedTransactions = computed(() => {
    if (!cardStore.cardTransactions || cardStore.cardTransactions.length === 0) {
        return [];
    }
    
    const transactions = [...cardStore.cardTransactions];
    const order = sortOrder.value === 'asc' ? 1 : -1;
    
    transactions.sort((a, b) => {
        let aValue, bValue;
        
        switch (activeTableTab.value) {
            case 'tx date/time':
                aValue = new Date(a.occurred_at).getTime();
                bValue = new Date(b.occurred_at).getTime();
                break;
            case 'status':
                aValue = (getStatusFromType(a.type) || '').toLowerCase();
                bValue = (getStatusFromType(b.type) || '').toLowerCase();
                break;
            case 'type':
                aValue = (a.type_description || a.type || '').toLowerCase();
                bValue = (b.type_description || b.type || '').toLowerCase();
                break;
            case 'mcc':
                aValue = (a.type || '').toLowerCase();
                bValue = (b.type || '').toLowerCase();
                break;
            case 'merchant name':
                aValue = (a.card_acceptor_location || '').toLowerCase();
                bValue = (b.card_acceptor_location || '').toLowerCase();
                break;
            case 'amount':
            case 'amount in aud':
                aValue = parseFloat(a.amount) || 0;
                bValue = parseFloat(b.amount) || 0;
                break;
            case 'reference':
                aValue = (a.reference || a.id || '').toLowerCase();
                bValue = (b.reference || b.id || '').toLowerCase();
                break;
            default:
                return 0;
        }
        
        if (aValue < bValue) return -1 * order;
        if (aValue > bValue) return 1 * order;
        return 0;
    });
    
    return transactions;
});

// 日期相關
const dateFrom = ref('');
const dateTo = ref('');

// 初始化日期：預設為當前月份的第一天到今天
const initializeDates = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    dateFrom.value = formatDateForInput(firstDay);
    dateTo.value = formatDateForInput(today);
};



// 載入交易資料
const loadTransactions = async (page = 1) => {
    const driverId = cardStore.cardInfo?.driver?.id || cardStore.cardInfo?.driver?.driver_id;
    if (!driverId) return;
    
    try {
        const params = {};
        if (dateFrom.value) {
            params.from = formatDateForAPI(dateFrom.value);
        }
        if (dateTo.value) {
            params.to = formatDateForAPI(dateTo.value);
        }
        if (page) {
            params.page = page;
        }
        const eaid = cardStore.cardInfo?.EAID ?? cardStore.cardInfo?.eaid;
        if (eaid != null && eaid !== '') {
            params.eaid = eaid;
        }

        await cardStore.fetchCardTransactions(driverId, params);
        
        // 同步更新分頁輸入框（使用 nextTick 確保 meta 已更新）
        await nextTick();
        if (cardStore.transactionsMeta?.current_page) {
            const newPage = parseInt(cardStore.transactionsMeta.current_page) || 1;
            if (newPage !== currentPageInput.value) {
                currentPageInput.value = newPage;
            }
        }
    } catch (error) {
        console.error('Failed to fetch card transactions:', error);
    }
};

// 處理日期變化
const handleDateChange = () => {
    // 驗證日期範圍：from 不能大於 to
    if (dateFrom.value && dateTo.value && dateFrom.value > dateTo.value) {
        console.warn('From date cannot be greater than To date');
        // 可以選擇交換日期或提示用戶
        return;
    }
    
    // 重置到第一頁
    currentPageInput.value = 1;
    loadTransactions(1);
};

// 分頁相關
const currentPageInput = ref(1);

// 計算分頁資訊
const paginationInfo = computed(() => {
    const meta = cardStore.transactionsMeta;
    if (!meta) return { current: 1, total: 1, totalItems: 0, start: 0, end: 0 };
    
    // 確保從 meta 中正確獲取數值，處理字符串轉數字
    const current = meta.current_page ? parseInt(meta.current_page) : 1;
    const total = meta.total_pages ? parseInt(meta.total_pages) : 1;
    const totalItems = meta.total_items ? parseInt(meta.total_items) : 0;
    const pageSize = meta.page_size ? parseInt(meta.page_size) : 20;
    const start = totalItems > 0 ? (current - 1) * pageSize + 1 : 0;
    const end = Math.min(current * pageSize, totalItems);
    
    return { current, total, totalItems, start, end };
});

const downloadTransactions = async () => {
  try{
    const driverId = cardStore.cardInfo?.driver?.id || cardStore.cardInfo?.driver?.driver_id;
    if (!driverId) return;
    const eaid = cardStore.cardInfo?.EAID ?? cardStore.cardInfo?.eaid;
    const payload = {
      driver_id: driverId,
      from: formatDateForAPI(dateFrom.value),
      to: formatDateForAPI(dateTo.value),
      ...(eaid != null && eaid !== '' ? { eaid } : {}),
    };
    await cardStore.downTransation(payload);
   
  }
  catch(error){
    console.error('Error downloading transactions:', error);
    throw error;
  }
}



// 監聽 paginationInfo 的變化，同步 currentPageInput
watch(() => paginationInfo.value.current, (newCurrent) => {
    if (newCurrent && newCurrent !== currentPageInput.value) {
        currentPageInput.value = newCurrent;
    }
});

// 初始化日期
initializeDates();

// 監聽 driver / EAID 變化（同 driver 多卡時需依 EAID 重載）
watch(
    () => [cardStore.cardInfo?.driver?.id, cardStore.cardInfo?.EAID ?? cardStore.cardInfo?.eaid],
    async ([newDriverId]) => {
        if (newDriverId) {
            await loadTransactions(1);
        }
    },
    { immediate: true }
);

// 添加一個 computed 來監控 cardTransactions 的變化
const transactionsCount = computed(() => {
    return cardStore.cardTransactions?.length || 0;
});

const getAmountClass = (amount) => {
    const amountValue = parseFloat(amount) || 0;
    return amountValue < 0 ? 'amount-negative' : 'amount-positive';
};

const formatSignedAmount = (amount) => {
    const amountValue = parseFloat(amount) || 0;
    const formattedAmount = formatAmount(amountValue);
    return amountValue < 0 ? `-${formattedAmount}` : formattedAmount;
};

//這找一下問題
const getStatusFromType = (type) => {
   
    if (!type) return 'N/A';
    // 可以根據 type 返回對應的狀態，這裡先返回 'Settled' 作為預設
    return 'Settled';
};

// 分頁處理
const changePage = (page) => {
    if (page >= 1) {
        currentPageInput.value = page;
        loadTransactions(page);
    }
};
</script>

<style scoped>
.card-transactions-container{
    background-color:#E6E9EC;
    border-radius:5px;
    padding:20px;
}
.card-transactions-header{
    display:flex;
    justify-content:space-between;
   
}
.card-transactions-header-date{
    display:flex;
    align-items:center;
    gap:10px;
}
.card-transactions-header select{
    padding:8px;
    border:none;
    border-radius:5px;
}
.date-input{
    padding:8px;
    border:1px solid #D0D5DD;
    border-radius:5px;
    font-size:14px;
    cursor:pointer;
}
.date-input:focus{
    outline:none;
    border-color:#5248DA;
}
.download-button{
    background-color:#00C962;
    color:black;
    border:none;
    border-radius:30px;
    padding:10px 20px;
    cursor:pointer;
    
}
.card-transactions-body{
    background-color:white;
    border-radius:5px;
    padding:20px;
    margin-top:20px;
}
.card-transactions-table{
    width: 100%;
    table-layout: fixed;
}
.card-transactions-table thead th{
    border-right: 1px solid #e4e7ec;
    padding: 10px;
}
.card-transactions-table thead th:last-child{
    border:none;
}
.card-transactions-table tbody td{
    padding-top:30px;
    border-bottom:1px solid #e4e7ec;
    padding-bottom:10px;
}
.card-transactions-table tbody td.amount-negative{
    color:#FF4040;
}
.card-transactions-table tbody td.amount-positive{
    color:#00C962;
}
.card-transactions-table thead th.active-table-tab{
    border-bottom:2px solid #00C962;
    color:#00C962;
}

.card-transactions-table thead th .muted{
    opacity:0.5;
}
.pagination-container{
    display:flex;
    justify-content:space-between;
    align-items:center;
    gap:10px;
    
    
}
.pagination-wrapper{
    display:flex;
    gap:10px;

}

/* 手機版響應式設計 */
@media (max-width: 768px) {
  .card-transactions-container {
    padding: 15px;
  }

  .card-transactions-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .card-transactions-header-date {
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-transactions-header h5,
  .card-transactions-header h6 {
    font-size: 14px;
    margin: 0;
  }

  .card-transactions-header select {
    padding: 6px;
    font-size: 13px;
  }

  .download-button {
    width: 100%;
    padding: 12px 20px;
    font-size: 14px;
    text-align: center;
  }

  .card-transactions-body {
    padding: 15px;
    margin-top: 15px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .card-transactions-table {
    min-width: 1000px;
    table-layout: auto;
  }

  .card-transactions-table thead th {
    padding: 12px 10px;
    font-size: 13px;
    min-width: 100px;
  }

  .card-transactions-table thead th .d-flex {
    gap: 8px;
  }

  .card-transactions-table thead th .fa {
    font-size: 12px;
  }

  .card-transactions-table tbody td {
    padding: 20px 10px 12px;
    font-size: 13px;
    white-space: nowrap;
    word-break: break-word;
  }

  /* 為每列設置最小寬度 */
  .card-transactions-table thead th:nth-child(1),
  .card-transactions-table tbody td:nth-child(1) {
    min-width: 140px; /* TX Date/Time */
  }

  .card-transactions-table thead th:nth-child(2),
  .card-transactions-table tbody td:nth-child(2) {
    min-width: 90px; /* Status */
  }

  .card-transactions-table thead th:nth-child(3),
  .card-transactions-table tbody td:nth-child(3) {
    min-width: 80px; /* Type */
  }

  .card-transactions-table thead th:nth-child(4),
  .card-transactions-table tbody td:nth-child(4) {
    min-width: 80px; /* MCC */
  }

  .card-transactions-table thead th:nth-child(5),
  .card-transactions-table tbody td:nth-child(5) {
    min-width: 130px; /* Merchant name */
  }

  .card-transactions-table thead th:nth-child(6),
  .card-transactions-table tbody td:nth-child(6) {
    min-width: 100px; /* Amount */
  }

  .card-transactions-table thead th:nth-child(7),
  .card-transactions-table tbody td:nth-child(7) {
    min-width: 120px; /* Amount in AUD */
  }

  .card-transactions-table thead th:nth-child(8),
  .card-transactions-table tbody td:nth-child(8) {
    min-width: 150px; /* Reference */
  }

  .pagination-container {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .pagination-wrapper {
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
  }

  .pagination-wrapper img {
    width: 20px !important;
    height: 20px !important;
  }

  .pagination-wrapper span {
    font-size: 14px;
  }

  .pagination-wrapper input {
    width: 50px !important;
    font-size: 14px;
  }

  .pagination-container p {
    font-size: 12px;
    text-align: center;
  }
}

/* 小手機版 (小於 576px) */
@media (max-width: 576px) {
  .card-transactions-container {
    padding: 10px;
  }

  .card-transactions-header h5,
  .card-transactions-header h6 {
    font-size: 12px;
  }

  .card-transactions-header select {
    padding: 5px;
    font-size: 12px;
  }

  .download-button {
    font-size: 12px;
    padding: 10px 15px;
  }

  .card-transactions-body {
    padding: 12px;
  }

  .card-transactions-table {
    min-width: 900px;
  }

  .card-transactions-table thead th {
    padding: 10px 8px;
    font-size: 12px;
    min-width: 90px;
  }

  .card-transactions-table thead th .fa {
    font-size: 11px;
  }

  .card-transactions-table tbody td {
    padding: 18px 8px 10px;
    font-size: 12px;
  }

  /* 小手機版列寬調整 */
  .card-transactions-table thead th:nth-child(1),
  .card-transactions-table tbody td:nth-child(1) {
    min-width: 130px;
  }

  .card-transactions-table thead th:nth-child(2),
  .card-transactions-table tbody td:nth-child(2) {
    min-width: 80px;
  }

  .card-transactions-table thead th:nth-child(3),
  .card-transactions-table tbody td:nth-child(3) {
    min-width: 70px;
  }

  .card-transactions-table thead th:nth-child(4),
  .card-transactions-table tbody td:nth-child(4) {
    min-width: 70px;
  }

  .card-transactions-table thead th:nth-child(5),
  .card-transactions-table tbody td:nth-child(5) {
    min-width: 120px;
  }

  .card-transactions-table thead th:nth-child(6),
  .card-transactions-table tbody td:nth-child(6) {
    min-width: 90px;
  }

  .card-transactions-table thead th:nth-child(7),
  .card-transactions-table tbody td:nth-child(7) {
    min-width: 110px;
  }

  .card-transactions-table thead th:nth-child(8),
  .card-transactions-table tbody td:nth-child(8) {
    min-width: 140px;
  }
}
</style>